import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleSchema, buildArticleBreadcrumbSchema } from '@/mocks/schema';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              ← Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 4);

  const articleSchema = article ? buildArticleSchema(article) : null;
  const breadcrumbSchema = article ? buildArticleBreadcrumbSchema(article) : null;

  return (
    <div className="min-h-screen bg-background-50">
      {articleSchema && <JsonLd data={articleSchema} />}
      {breadcrumbSchema && <JsonLd data={breadcrumbSchema} />}
      <Navbar />

      <main>
        <article className="pt-24 pb-16">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                    Home
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li>
                  <Link to="/#articles" className="hover:text-primary-500 transition-colors cursor-pointer">
                    Articles
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-800 font-medium truncate max-w-[200px]">
                  {article.title}
                </li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${article.tagColor}`}>
                  {article.tag}
                </span>
                <span className="text-xs text-foreground-500">•</span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
                <span className="text-xs text-foreground-500">•</span>
                <span className="text-xs text-foreground-500">Updated June 2026</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 text-sm font-bold">AE</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">ACBuySpreadsheet Editorial Team</p>
                  <p className="text-xs text-foreground-500">Fact Checked • Reviewed by Shopping Experts</p>
                </div>
              </div>
            </header>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, idx) => (
                <section key={idx} className="mb-12">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pidx) => (
                    <p
                      key={pidx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ __html: para }}
                    ></p>
                  ))}
                </section>
              ))}

              <section className="mb-12 bg-primary-50 rounded-2xl p-6 md:p-8 text-center">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                  Ready to Start Shopping?
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-lg mx-auto">
                  Now that you have explored this <strong>acbuy spreadsheet</strong> resource, you are ready to put your knowledge into action. Browse the directory or jump directly to KASpreadsheet.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    ← Back to Homepage
                  </Link>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-3 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Shop on KASpreadsheet →
                  </a>
                </div>
              </section>

              <section className="border-t border-background-200/70 pt-8">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">
                  Read Next
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link
                    to="/"
                    className="flex items-center gap-2 px-4 py-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-primary-400"></i>
                    ← Back to Homepage
                  </Link>
                  <Link
                    to="/acbuy-spreadsheet-guide"
                    className="flex items-center gap-2 px-4 py-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-primary-400"></i>
                    Complete ACBuy Spreadsheet Guide 2026
                  </Link>
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="flex items-center gap-2 px-4 py-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-primary-400"></i>
                      {ra.title}
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}